/* Service to validate the selected standard review excel file. */
/* Added by: shashank.b.suresh on 2024-06-14 */

import { Injectable, inject } from '@angular/core';
import * as XLSX from 'xlsx';
import { firstValueFrom } from 'rxjs';
import { ReviewerService } from './reviewer.service';
import { RebarAuthService } from '../rebarauth/rebar.auth.service';

export interface ValidationError {
  row?: number;
  reviewName?: string;
  field?: string;
  error: string;
}

export interface ValidationResult {
  validation: 'success' | 'failed';
  createdCount?: number;
  errors?: ValidationError[];
}

interface ProposedYearOption {
  DisplayName: string;
  ProposedYear: number;
  ProposedYearId: number;
}

export interface DataRow {
  row: number;
  data: any;
}

export interface ParsedWorkbook {
  headers: string[];
  rows: DataRow[];
}

export interface CommonField {
  FieldName: string;
  FieldValue: string;
  DataType: string;
}

export interface LocationField {
  LocationName: string;
  IsParentLocation: 0 | 1;
  FieldName: string;
  FieldValue: string;
  DataType: string;
  RowNumber: number;
}

export interface BulkUploadReview {
  ReviewStatus: 'Draft';
  CreatedBy: string;
  ReviewName: string;
  CommonFields: CommonField[];
  LocationFields: LocationField[];
}

export interface BulkUploadParams {
  category: 'ReviewSTD_BulkUpload';
  reviews: BulkUploadReview[];
}

export interface BulkUploadPayload {
  params: BulkUploadParams;
}

const MAX_FILE_SIZE_MB = 20;
const MAX_RECORDS = 100;
const VALID_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
// The template's standard review data sheet
const DATA_SHEET_NAME = 'Bulk_Upload_STD_Review';
// The template's instructions tab
const GUIDELINE_SHEET_NAME = 'Guideline';
const MANDATORY_COLUMN_START = 0;
const MANDATORY_COLUMN_END = 10;

// Column positions (0-based) for fields with a specific value-format rule.
const COLUMN_AUTO_NUMBER = 0; // A
const COLUMN_REVIEW_NAME = 1; // B
const COLUMN_PROPOSED_YEAR = 2; // C
const COLUMN_MASTER_CLIENT_NAME = 3; // D
const COLUMN_PROJECT_NAME = 4; // E
const COLUMN_CONTRACT_NUMBER = 5; // F
const COLUMN_MARKET = 6; // G
const COLUMN_MARKET_UNIT = 7; // H
const COLUMN_DELIVERY_COUNTRY = 8; // I
const COLUMN_LOCATION_TO_BE_REVIEWED = 9; // J
const COLUMN_IS_PARENT_LOCATION = 10; // K
const COLUMN_PRIMARY_REVIEWER = 11; // L
const COLUMN_PARENT_ID = 12; // M

// Display names keyed by Excel column letter. Update here when the template changes.
const COLUMN_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  A: 'AutoNumber',
  B: 'Review Name',
  C: 'Proposed Year',
  D: 'Master Client Name',
  E: 'D&T Project Name',
  F: 'Contract Number',
  G: 'Market',
  H: 'Market Unit',
  I: 'Delivery Country',
  J: 'Locations To Be Reviewed',
  K: 'Is It Parent Location?',
  L: 'Primary Reviewer',
  M: 'Parent Id',
};

// Exact header text expected in row 2 , updated to match the template. Update here when the template changes.
const EXPECTED_HEADER_ROW: readonly string[] = [
  'AutoNumber (Read Only)',
  'REVIEW NAME * (Auto: C–D–E–J)',
  'PROPOSED YEAR *',
  'MASTER CLIENT NAME *',
  'D&T PROJECT NAME *',
  'CONTRACT NUMBER *',
  'MARKET *',
  'MARKET UNIT *',
  'DELIVERY COUNTRY *',
  'LOCATIONS TO BE REVIEWED *',
  'IS IT PARENT LOCATION * ?',
  'PRIMARY REVIEWER',
  'PARENT ID',
];

// Mapping of field names returned by the API to user-friendly display names for error messages.
const ERROR_RESPONSE_FIELD_NAME_MAP: Readonly<Record<string, string>> = {
  'ClientName':           'Master Client Name',
  'ProjectName':          'D&T Project Name',
  'ContractNumber':       'Contract Number',
  'Market':               'Market',
  'MarketUnit':           'Market Unit',
  'DeliveryCountry':      'Delivery Country',
  'LocationToBeReviewed': 'Locations To Be Reviewed',
  'RCReviewer':           'Primary Reviewer',
};

// Regex patterns for field value validation.
const PROPOSED_YEAR_PATTERN = /^FY\d{2}$/;
const YES_NO_PATTERN = /^(Yes|No)$/i;
const CONTRACT_NUMBER_PATTERN = /^\d{10}$/;
const NUMBER_PATTERN = /^\d+$/;
const REVIEWER_PATTERN = /^[a-zA-Z0-9.]+$/;

@Injectable({ providedIn: 'root' })
export class StandardReviewImportValidationService {

  private readonly reviewerService = inject(ReviewerService);
  private readonly createdBy = inject(RebarAuthService).getUserName() ?? '';

  private cachedProposedYears: Set<string> | null = null;
  private bulkUploadApiResponse: any[] = [];

  constructor() { this.loadProposedYears(); }
  private proposedYearIdMap: Map<string, number> = new Map();

  async loadProposedYears(): Promise<boolean> {
    if (this.cachedProposedYears) return true;
    try {
      const years = await firstValueFrom(this.reviewerService.getPurposedYear()) as ProposedYearOption[];
      this.cachedProposedYears = new Set(years.map((y) => String(y.DisplayName).trim()));
      this.proposedYearIdMap = new Map(years.map((y) => [String(y.DisplayName).trim(), y.ProposedYearId]));
      return true;
    } catch (error) {
      console.error('Failed to load proposed years:', error);
      return false;
    }
  }

  async validateStandardReviewUpload(file: File): Promise<ValidationResult> {

    const yearsLoaded = await this.loadProposedYears();
    if (!yearsLoaded) {
      return {
        validation: 'failed',
        errors: [{ error: 'Failed to retrieve Proposed Year options. Please refresh the page and try again.' }],
      };
    }

    // Basic file checks (extension, MIME, size)
    const fileError = this.validateFile(file);
    if (fileError) {
      return { validation: 'failed', errors: [{ error: fileError }] };
    }

    let standardReviewData: ParsedWorkbook;
    try {
      // Extract data from the Excel file
      standardReviewData = await this.parseWorkbook(file);
    } catch {
      return {
        validation: 'failed',
        errors: [{ error: 'Outdated template version. Please download the latest template and try again.' }],
      };
    }

    // No Records Validation
    if (standardReviewData.rows.length === 0) {
      return {
        validation: 'failed',
        errors: [{ error: 'The uploaded file has no records. Please add at least one review and try again.' }],
      };
    }

    // Max Records Validation
    if (standardReviewData.rows.length > MAX_RECORDS) {
      return {
        validation: 'failed',
        errors: [
          {
            error: `Record limit exceeded. Maximum ${MAX_RECORDS} records allowed per upload. Please split and upload separately`,
          },
        ],
      };
    }

    const rowLevelErrors = [
      ...this.validateRowFields(standardReviewData),
      ...this.validateDuplicateRows(standardReviewData),
    ];

    // Mandatory fields and format validation errors
    if (rowLevelErrors.length > 0) {
      return { validation: 'failed', errors: rowLevelErrors };
    }

    // Parent ID reference validation is done after row-level validation
    const parentReferenceErrors = this.validateParentIdReferences(standardReviewData);
    if (parentReferenceErrors.length > 0) {
      return { validation: 'failed', errors: parentReferenceErrors };
    }

    let payload: BulkUploadPayload;
    try {
      payload = this.buildPayload(standardReviewData as ParsedWorkbook);
    } catch {
      return {
        validation: 'failed',
        errors: [{ error: 'Failed to prepare upload data. Please try again.' }],
      };
    }

    // Call the API to draft the standard review bulk upload
    const apiSuccess = await this.draftStandardReviewBulkUpload(payload.params);
    if (!apiSuccess) {
      return { validation: 'failed', errors: [{ error: 'Service error. Please try again.' }] };
    }

    // Map the API response to the ValidationResult format
    try {
      return this.mapApiResponse();
    } catch {
      return { validation: 'failed', errors: [{ error: 'Invalid server response. Please try again.' }] };
    }
  }

  
  // Basic file checks (extension, MIME, size). Returns an error message, or null if OK. */
  private validateFile(file: File): string | null {
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
    if (!isXlsx || (file.type && file.type !== VALID_MIME)) {
      return 'Invalid file format. Please upload a .xlsx file only';
    }
    if (file.size === 0) {
      return 'The uploaded file is empty. Please upload a valid .xlsx file.';
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size exceeds the allowed limit of ${MAX_FILE_SIZE_MB} MB. Please split the file and upload separately`;
    }
    return null;
  }

  // Read the workbook and split the data sheet into its header row and data rows. 
  private async parseWorkbook(file: File): Promise<ParsedWorkbook> {
    
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });

    const requiredSheets = [DATA_SHEET_NAME, GUIDELINE_SHEET_NAME];
    const hasExactSheets = workbook.SheetNames.length === requiredSheets.length
      && requiredSheets.every(name => workbook.SheetNames.includes(name));
    if (!hasExactSheets) {
      throw new Error('Invalid excel file.');
    }

    const worksheet = workbook.Sheets[DATA_SHEET_NAME];

    const headerError = this.validateHeaderRow(worksheet);
    if (headerError) {
      throw new Error(headerError);
    }

    return this.extractHeadersAndRows(worksheet);
  }


  private validateHeaderRow(worksheet: XLSX.WorkSheet): string | null {
    const HEADER_ROW_NUMBER = 2;

    // Validate that the header row matches the expected header values and order
    for (let col = 0; col < EXPECTED_HEADER_ROW.length; col++) {
      const address = XLSX.utils.encode_cell({ r: HEADER_ROW_NUMBER - 1, c: col });
      const cell = worksheet[address];
      const actual = String(cell?.v ?? '').replace(/\r\n|\r|\n/g, ' ').replace(/\s+/g, ' ').trim();
      if (actual !== EXPECTED_HEADER_ROW[col]) {
        return 'Invalid excel file.';
      }
    }

    // Check for any extra columns beyond the expected header row and throw an error
    const range = XLSX.utils.decode_range(worksheet['!ref'] ?? 'A1');
    for (let col = EXPECTED_HEADER_ROW.length; col <= range.e.c; col++) {
      const address = XLSX.utils.encode_cell({ r: HEADER_ROW_NUMBER - 1, c: col });
      const value = String(worksheet[address]?.v ?? '').trim();
      if (value !== '') {
        return 'Invalid excel file.';
      }
    }

    return null;
  }


  // Uses header: 1 so blank rows aren't silently skipped, keeping row numbers accurate. 
  private extractHeadersAndRows(worksheet: XLSX.WorkSheet): ParsedWorkbook {
    const allRows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1, range: 1 });

    const headers = (allRows[0] ?? [])
      .map((_header, col) => this.getFieldDisplayName(col))
      .filter((header) => header !== '');
    const dataRowCells = allRows.slice(1);

    // Filter out completely blank rows, but keep partially filled rows for validation.
    const rows: DataRow[] = dataRowCells
      .map((cells, index) => {
        const data: any = {};
        headers.forEach((header, col) => {
          data[header] = cells[col] ?? '';
        });
        return { row: index + 3, data };
      })
      .filter(({ data }) => Object.values(data).some((value) => String(value).trim() !== ''));

    return { headers, rows };
  }

  private getFieldDisplayName(colIndex: number): string {
    const colLetter = String.fromCharCode(65 + colIndex);
    return COLUMN_DISPLAY_NAMES[colLetter] ?? '';
  }

  private validateRowFields({ headers, rows }: ParsedWorkbook): ValidationError[] {
    const errors: ValidationError[] = [];

    // If Auto-Generated fields are blank or excel formulas bypassed
    const autoNumberField = headers[COLUMN_AUTO_NUMBER];
    const reviewNameField = headers[COLUMN_REVIEW_NAME];
    // Source fields for the Review Name auto-formula (C-D-E-J)
    const proposedYearField = headers[COLUMN_PROPOSED_YEAR];
    const masterClientNameField = headers[COLUMN_MASTER_CLIENT_NAME];
    const projectNameField = headers[COLUMN_PROJECT_NAME];
    const locationToBeReviewedField = headers[COLUMN_LOCATION_TO_BE_REVIEWED];
    // Mandatory check starts from column C
    const mandatoryFields = headers.slice(MANDATORY_COLUMN_START + 2, MANDATORY_COLUMN_END + 1);

    const fieldRules: ReadonlyArray<{ field: string; pattern: RegExp; message: string }> = [
      { field: headers[COLUMN_PROPOSED_YEAR], pattern: PROPOSED_YEAR_PATTERN, message: 'must be in FY00 format (e.g. FY25).' },
      { field: headers[COLUMN_CONTRACT_NUMBER], pattern: CONTRACT_NUMBER_PATTERN, message: 'must be a number with exactly 10 digits.' },
      { field: headers[COLUMN_IS_PARENT_LOCATION], pattern: YES_NO_PATTERN, message: 'must be either Yes or No.' },
      { field: headers[COLUMN_PARENT_ID], pattern: NUMBER_PATTERN, message: 'must be a number.' },
      { field: headers[COLUMN_PRIMARY_REVIEWER], pattern: REVIEWER_PATTERN, message: `must not contain special characters (e.g. @, $, &, #).` },
    ];

    const seenAutoNumbers = new Map<string, number>();

    for (const { row, data } of rows) {
      const reviewName = String(data['Review Name'] ?? '').trim();
      const autoNumberValue = String(data[autoNumberField] ?? '').trim();

      // Validate Auto-Number field
      if (!autoNumberValue) {
        errors.push({ row, reviewName, field: autoNumberField, error: `${autoNumberField} is an auto-generated field but is empty in the excel file.` });
      } else if (!NUMBER_PATTERN.test(autoNumberValue)) {
        errors.push({ row, reviewName, field: autoNumberField, error: `${autoNumberField} must be a number.` });
      } else {
        const firstRow = seenAutoNumbers.get(autoNumberValue);
        if (firstRow !== undefined) {
          errors.push({ row, reviewName, field: autoNumberField, error: `${autoNumberField} must be unique. Value ${autoNumberValue} is already used in row ${firstRow}.` });
        } else {
          seenAutoNumbers.set(autoNumberValue, row);
        }
      }

      // Validate Review Name auto-formula (C-STD-D-E-J)
      if (!reviewName) {
        errors.push({ row, reviewName, field: reviewNameField, error: `${reviewNameField} is an auto-generated field but is empty in the excel file.` });
      } else {
        // Review Name must equal Proposed Year-STD-Master Client Name-D&T Project Name-Location, joined by '-'.
        const sourceValues = [proposedYearField, masterClientNameField, projectNameField, locationToBeReviewedField]
          .map((field) => String(data[field] ?? '').trim());
        const squash = (value: string) => value.replace(/\s+/g, '').toLowerCase();
        const expectedReviewName = [sourceValues[0], 'STD', sourceValues[1], sourceValues[2], sourceValues[3]].join('-');

        if (sourceValues.every((value) => Boolean(value)) && squash(reviewName) !== squash(expectedReviewName)) {
          errors.push({ row, reviewName, field: reviewNameField, error: `${reviewNameField} is an auto-generated field and does not match the expected format (C-STD-D-E-J).` });
        }
      }

      // Validate mandatory fields (columns C–K)
      for (const field of mandatoryFields) {
        if (String(data[field] ?? '').trim() === '') {
          errors.push({ row, reviewName, field, error: `${field} is mandatory and must be filled.` });
        }
      }

      // Validate specific field value formats
      for (const { field, pattern, message } of fieldRules) {
        const value = String(data[field] ?? '').trim();
        if (value && !pattern.test(value)) {
          errors.push({ row, reviewName, field, error: `${field} ${message}` });
        }
      }

      // Validate Proposed Year against system values (only if format is valid)
      const proposedYearValue = String(data[proposedYearField] ?? '').trim();
      if (proposedYearValue && PROPOSED_YEAR_PATTERN.test(proposedYearValue) && this.cachedProposedYears && !this.cachedProposedYears.has(proposedYearValue)) {
        errors.push({ row, reviewName, field: proposedYearField, error: `${proposedYearField} is not available in the system. Please select a valid fiscal year.` });
      }
    }

    return errors;
  }

  private validateDuplicateRows({ headers, rows }: ParsedWorkbook): ValidationError[] {
    const errors: ValidationError[] = [];
    const seen = new Map<string, number>();
    const keyFields = headers.slice(COLUMN_PROPOSED_YEAR, COLUMN_PARENT_ID + 1);

    for (const { row, data } of rows) {
      const key = keyFields.map((field) => String(data[field] ?? '').trim().toLowerCase()).join('\0');
      const firstRow = seen.get(key);
      if (firstRow !== undefined) {
        const reviewName = String(data['Review Name'] ?? '').trim();
        errors.push({ row, reviewName, field: 'All', error: `Duplicate row detected. This row is identical to row ${firstRow}.` });
      } else {
        seen.set(key, row);
      }
    }

    return errors;
  }

  private validateParentIdReferences({ headers, rows }: ParsedWorkbook): ValidationError[] {
    const errors: ValidationError[] = [];
    const isParentLocationField = headers[COLUMN_IS_PARENT_LOCATION];
    const parentIdField = headers[COLUMN_PARENT_ID];
    const autoNumberField = headers[COLUMN_AUTO_NUMBER];
    const deliveryCountryField = headers[COLUMN_DELIVERY_COUNTRY];
    const locationField = headers[COLUMN_LOCATION_TO_BE_REVIEWED];

    // Group key: columns C–H (Proposed Year → Market Unit), shared by parent and child rows.
    const groupKeyFields = headers.slice(COLUMN_PROPOSED_YEAR, COLUMN_MARKET_UNIT + 1);
    const toKey = (data: any) => groupKeyFields.map(f => String(data[f] ?? '').trim().toLowerCase()).join('\0');

    // Group rows by their C–H key.
    const groups = new Map<string, { row: number; reviewName: string; autoNumber: string; isParent: boolean; parentId: string }[]>();
    for (const { row, data } of rows) {
      const key = toKey(data);
      const entry = {
        row,
        reviewName: String(data['Review Name'] ?? '').trim(),
        autoNumber: String(data[autoNumberField] ?? '').trim(),
        isParent: String(data[isParentLocationField] ?? '').trim().toLowerCase() === 'yes',
        parentId: String(data[parentIdField] ?? '').trim(),
      };
      const group = groups.get(key) ?? [];
      group.push(entry);
      groups.set(key, group);
    }

    const rowDataByNumber = new Map(rows.map(r => [r.row, r.data]));

    for (const group of groups.values()) {
      // Rule: parent ID must be blank when Is Parent Location = Yes.
      for (const { row, reviewName, isParent, parentId } of group) {
        if (isParent && parentId) {
          errors.push({ row, reviewName, field: parentIdField, error: `${parentIdField} must be blank when ${isParentLocationField} is set to Yes.` });
        }
      }

      // Rule: a single-entry group must be marked as parent.
      if (group.length === 1) {
        const [{ row, reviewName, isParent }] = group;
        if (!isParent) {
          errors.push({ row, reviewName, field: isParentLocationField, error: `${isParentLocationField} must be set to Yes for a single-location review.`});
        }
      }

      // Rule: only one location per group can be marked as parent.
      const parentEntries = group.filter(e => e.isParent);
      if (parentEntries.length > 1) {
        for (const { row, reviewName } of parentEntries) {
          errors.push({ row, reviewName, field: isParentLocationField, error: `Only one location can be set as a parent.` });
        }
      }

      // Rule: multi-entry group must have at least one parent.
      if (group.length > 1 && parentEntries.length === 0) {
        for (const { row, reviewName } of group) {
          errors.push({ row, reviewName, field: isParentLocationField, error: `At least one location must be set as a parent.` });
        }
      }

      // Rule: child's Parent ID must be filled and match the parent's AutoNumber within the same group.
      const parentEntry = group.find(e => e.isParent);
      for (const { row, reviewName, isParent, parentId } of group) {
        if (!isParent) {
          if (!parentId) {
            errors.push({ row, reviewName, field: parentIdField, error: `${parentIdField} is required when ${isParentLocationField} is set to No.` });
          } else if (!parentEntry || parentId !== parentEntry.autoNumber) {
            errors.push({ row, reviewName, field: parentIdField, error: `${parentIdField} must match the AutoNumber of the parent location.` });
          }
        }
      }

      // Rule: Delivery Country + Location combination must be distinct across all rows in a group (parent and children).
      const seenLocations = new Map<string, number>();
      for (const { row, reviewName } of group) {
        const rowData = rowDataByNumber.get(row);
        const country = String(rowData?.[deliveryCountryField] ?? '').trim().toLowerCase();
        const location = String(rowData?.[locationField] ?? '').trim().toLowerCase();
        const locationKey = `${country}\0${location}`;

        const firstRow = seenLocations.get(locationKey);
        if (firstRow !== undefined) {
          errors.push({ row, reviewName, field: locationField, error: `Duplicate location found. Row ${firstRow} already has this Delivery Country and Location combination.` });
        } else {
          seenLocations.set(locationKey, row);
        }
      }
    }

    return errors;
  }


  // ---- Payload construction for API Request ----

  buildPayload(validatedBulkUploadData: ParsedWorkbook): BulkUploadPayload {
    const payload: BulkUploadPayload = {
      params: {
        category: 'ReviewSTD_BulkUpload',
        reviews: this.buildReviews(validatedBulkUploadData),
      },
    };
    return payload;
  }

  private buildReviews({ headers, rows }: ParsedWorkbook): BulkUploadReview[] {
    const groupKeyFields = headers.slice(COLUMN_PROPOSED_YEAR, COLUMN_MARKET_UNIT + 1);

    // group rows by their common fields (Proposed Year to Market Unit) to identify multi-location reviews.
    const groups = new Map<string, DataRow[]>();
    for (const dataRow of rows) {
      const key = groupKeyFields.map(f => String(dataRow.data[f] ?? '').trim().toLowerCase()).join('\0');
      const group = groups.get(key) ?? [];
      group.push(dataRow);
      groups.set(key, group);
    }

    // Build a BulkUploadReview for each group of rows
    return Array.from(groups.values(), group => this.buildReview(headers, group));
  }

  private buildReview(headers: string[], group: DataRow[]): BulkUploadReview {
    const parentRow = group.find(({ data }) =>
      String(data[headers[COLUMN_IS_PARENT_LOCATION]] ?? '').trim().toLowerCase() === 'yes'
    ) ?? group[0];

    const { data } = parentRow;
    const proposedYearValue = String(data[headers[COLUMN_PROPOSED_YEAR]] ?? '').trim();
    const proposedYearId = this.proposedYearIdMap.get(proposedYearValue) ?? 0;
    const isMultiLocation = group.length > 1;

    const isParentField = headers[COLUMN_IS_PARENT_LOCATION];
    const countryField  = headers[COLUMN_DELIVERY_COUNTRY];
    const locationField = headers[COLUMN_LOCATION_TO_BE_REVIEWED];
    const reviewerField = headers[COLUMN_PRIMARY_REVIEWER];

    return {
      ReviewStatus: 'Draft',
      CreatedBy: this.createdBy,
      ReviewName: String(data[headers[COLUMN_REVIEW_NAME]] ?? '').trim(),
      CommonFields: [
        { FieldName: 'ReviewTypeID',            FieldValue: '1',                                                              DataType: 'int'      },
        { FieldName: 'ProposedYearId',           FieldValue: String(proposedYearId),                                           DataType: 'int'      },
        { FieldName: 'ClientName',               FieldValue: String(data[headers[COLUMN_MASTER_CLIENT_NAME]] ?? '').trim(),    DataType: 'nvarchar' },
        { FieldName: 'ProjectName',              FieldValue: String(data[headers[COLUMN_PROJECT_NAME]] ?? '').trim(),          DataType: 'nvarchar' },
        { FieldName: 'ContractNumber',           FieldValue: String(data[headers[COLUMN_CONTRACT_NUMBER]] ?? '').trim(),       DataType: 'nvarchar' },
        { FieldName: 'Market',                   FieldValue: String(data[headers[COLUMN_MARKET]] ?? '').trim(),                DataType: 'nvarchar' },
        { FieldName: 'MarketUnit',               FieldValue: String(data[headers[COLUMN_MARKET_UNIT]] ?? '').trim(),           DataType: 'nvarchar' },
        { FieldName: 'SingleMultipleLocation',   FieldValue: isMultiLocation ? 'Multiple Location' : 'Single Location',       DataType: 'nvarchar' },
      ],

      // Build LocationFields for each row in the group
      LocationFields: group.flatMap(({ row, data: rowData }) => {
        const locationName = String(rowData[locationField] ?? '').trim();
        const isParent = (String(rowData[isParentField] ?? '').trim().toLowerCase() === 'yes' ? 1 : 0) as 0 | 1; // Convert to 0 or 1 
        return [
          { LocationName: locationName, IsParentLocation: isParent, FieldName: 'DeliveryCountry',      FieldValue: String(rowData[countryField]  ?? '').trim(), DataType: 'nvarchar', RowNumber: row },
          { LocationName: locationName, IsParentLocation: isParent, FieldName: 'LocationToBeReviewed', FieldValue: locationName,                                DataType: 'nvarchar', RowNumber: row },
          { LocationName: locationName, IsParentLocation: isParent, FieldName: 'RCReviewer',           FieldValue: String(rowData[reviewerField] ?? '').trim(), DataType: 'nvarchar', RowNumber: row },
        ];
      }),
    };
  }


  //------------ API Calling and Response Mapping-------------------
  private async draftStandardReviewBulkUpload(params: BulkUploadParams): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.reviewerService.draftStandardReviewBulkUpload(params));
      if (!Array.isArray(response) || response.length === 0) {
        throw new Error('Empty or invalid response from API');
      }
      this.bulkUploadApiResponse = response;
      return true;
    } catch {
      return false;
    }
  }

  private mapApiResponse(): ValidationResult {

    // SP returns a single row with Status = 'Success' if all records are valid.
    if (this.bulkUploadApiResponse[0]?.Status === 'Success') {
      return { validation: 'success', createdCount: this.bulkUploadApiResponse[0]?.Totalrecordscreated ?? 0 };
    }

    // If there are errors, map them to the ValidationError format.
    const errors: ValidationError[] = this.bulkUploadApiResponse.flatMap(item => {
      const rowNumbers  = String(item.RowNumber).split(',');
      // Split the review names by '/' to handle multiple review names
      const reviewNames = String(item.ReviewName).split('/');

      // Ensure that the number of row numbers matches the number of review names for each error item.
      if (rowNumbers.length !== reviewNames.length) {
        throw new Error(`Row number and review name count mismatch for error: ${item.ErrorMessage}`);
      }
      const mappedField = this.mapFieldNames(item.FieldName); 
      return rowNumbers.map((rowNum, index) => ({
        row:        Number(rowNum.trim()),
        reviewName: reviewNames[index].trim(),
        field:      mappedField,
        error:      item.ErrorMessage,
      }));
    });

    return { validation: 'failed', errors };
  }

  private mapFieldNames(fieldName: string): string {
    return fieldName
      ?.split(',')
      .map((f: string) => {
        const normalized = f.trim().replace(/\s+/g, '');
        return ERROR_RESPONSE_FIELD_NAME_MAP[normalized] ?? f.trim();
      })
      .join(', ') ?? fieldName;
  }

}